import { useState } from 'react';
import { Images, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { TID, type MansionPhoto } from '@yalidavet/shared';
import { GALLERY } from '@/lib/images';

export default function Gallery({ photos, title }: { photos: MansionPhoto[]; title: string }) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const shown = photos.slice(0, 5);
  const placeholder = GALLERY[0];

  if (photos.length === 0) {
    return <div className="yd-skeleton h-72 w-full" data-testid={TID.gallery} />;
  }

  return (
    <>
      <div className="relative">
        <div className="yd-gallery overflow-hidden rounded-xl2" data-testid={TID.gallery} data-photo-count={photos.length}>
          {shown.map((p, i) => (
            <button
              key={p.id}
              type="button"
              className="group relative overflow-hidden bg-ink-100"
              onClick={() => { setIndex(i); setOpen(true); }}
              aria-label={`${title} fotoğraf ${i + 1}`}
            >
              <img
                src={p.url || placeholder}
                alt={p.altText ?? title}
                loading={i === 0 ? 'eager' : 'lazy'}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => { setIndex(0); setOpen(true); }}
          className="yd-btn-secondary absolute bottom-4 right-4 shadow"
          data-testid={TID.galleryAll}
        >
          <Images className="h-4 w-4" aria-hidden />
          Tüm fotoğraflar ({photos.length})
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Fotoğraf galerisi"
          data-testid="gallery-lightbox"
          onClick={() => setOpen(false)}
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            onClick={() => setOpen(false)}
            aria-label="Kapat"
            data-testid="gallery-close"
          >
            <X className="h-5 w-5" />
          </button>

          <button
            type="button"
            className="absolute left-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/20"
            onClick={(e) => { e.stopPropagation(); setIndex((i) => (i - 1 + photos.length) % photos.length); }}
            aria-label="Önceki fotoğraf"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <figure className="max-h-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img src={photos[index]?.url ?? placeholder} alt={photos[index]?.altText ?? title} className="max-h-[80vh] rounded-lg object-contain" />
            <figcaption className="mt-3 text-center text-sm text-white/70">
              {index + 1} / {photos.length}
            </figcaption>
          </figure>

          <button
            type="button"
            className="absolute right-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/20"
            onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % photos.length); }}
            aria-label="Sonraki fotoğraf"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      )}
    </>
  );
}
